// components/AboutMe.js
import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";

const isInternalNetwork = () => {
  const hostname = window.location.hostname;
  return hostname === "testreact.home-k8s.lab";
};

const API_URL = isInternalNetwork()
  ? process.env.REACT_APP_API_URL_INTERNAL
  : process.env.REACT_APP_API_URL_EXTERNAL;

const LoadingSkeleton = () => (
  <div className="animate-pulse space-y-4">
    <div className="flex items-center space-x-4">
      <div className="h-12 w-12 bg-gray-700 rounded-full" />
      <div className="h-4 w-[250px] bg-gray-700 rounded" />
    </div>
    {[1, 2, 3, 4, 5, 6, 7].map((i) => (
      <div key={i} className="h-4 w-full bg-gray-700 rounded" />
    ))}
  </div>
);

const UserInfoRow = ({ label, value }) => (
  <div className="flex items-center space-x-4 py-3 border-b border-gray-700 last:border-b-0">
    <span className="text-sm font-medium text-gray-400 w-1/3">{label}:</span>
    <span className="text-sm text-blue-300">{value}</span>
  </div>
);

export const AboutMe = () => {
  const { token, logout } = useAuth();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            logout();
            return;
          }
          throw new Error("Failed to fetch user data");
        }

        const data = await response.json();
        setUserData(data);
      } catch (error) {
        setError("Failed to load user information");
        console.error("Error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [token, logout]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 py-8">
        <div className="max-w-2xl mx-auto bg-gray-800 rounded-lg shadow-xl">
          <div className="p-6">
            <div className="text-red-400 bg-red-900/50 p-3 rounded border border-red-800/50">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-2xl mx-auto bg-gray-800 rounded-lg shadow-xl">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-100">About Me</h2>
            <button
              onClick={() => window.history.back()}
              className="bg-gray-700 hover:bg-gray-600 text-gray-200 px-4 py-2 rounded transition-colors"
            >
              Back
            </button>
          </div>
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <div className="space-y-4">
              {/* 用户头部信息 */}
              <div className="flex items-center space-x-4 mb-6 bg-gray-700/50 p-4 rounded-lg">
                <div className="relative h-12 w-12">
                  {userData.avatar_url ? (
                    <>
                      <img
                        src={userData.avatar_url}
                        alt={userData.username}
                        className="h-12 w-12 rounded-full object-cover"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <div className="hidden h-12 w-12 rounded-full bg-blue-600 text-gray-100 text-lg items-center justify-center">
                        {userData.username[0].toUpperCase()}
                      </div>
                    </>
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-blue-600 text-gray-100 text-lg flex items-center justify-center">
                      {userData.username[0].toUpperCase()}
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-gray-100">
                    {userData.username}
                  </h3>
                  <p className="text-sm text-blue-300">{userData.email}</p>
                </div>
              </div>

              {/* 用户详细信息 */}
              <div className="bg-gray-700/30 rounded-lg p-4">
                <UserInfoRow label="API Quota" value={userData.api_quota} />
                <UserInfoRow label="Provider" value={userData.provider} />
                <UserInfoRow
                  label="Bio"
                  value={userData.bio || "No bio provided"}
                />
                <UserInfoRow
                  label="Role"
                  value={
                    userData.role.charAt(0).toUpperCase() +
                    userData.role.slice(1)
                  }
                />
                <UserInfoRow
                  label="Status"
                  value={
                    userData.status.charAt(0).toUpperCase() +
                    userData.status.slice(1)
                  }
                />
                <UserInfoRow
                  label="Last Login"
                  value={new Date(userData.last_login).toLocaleString()}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AboutMe;
